// src/components/LeereBoxenListe.tsx
import React, { useEffect, useState, ChangeEvent } from 'react';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { fetchApi } from './fetchApi';


interface LeereBox {
  id: number;
  lagerort_id: number;
  lagerort_name: string;
  regalnummer: string | null;
  fachnummer: string | null;
  boxnummer: string;
}

interface EditForm {
  regalnummer: string;
  fachnummer: string;
}

export default function LeereBoxenListe() {
  const [boxen, setBoxen] = useState<LeereBox[]>([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({ regalnummer: '', fachnummer: '' });
  const [speichernLaeuft, setSpeichernLaeuft] = useState(false);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteBox, setDeleteBox] = useState<LeereBox | null>(null);
  const [loeschenLaeuft, setLoeschenLaeuft] = useState(false);

  async function ladeDaten() {
    setLaden(true);
    setFehler(null);

    try {
      const res = await fetchApi('/api/view/leere-boxen');
      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.error || 'Fehler beim Laden der leeren Boxen.');
      }

      setBoxen(Array.isArray(json) ? json : []);
    } catch (err: any) {
      console.error('❌ Fehler beim Laden der leeren Boxen:', err);
      setFehler(
  err?.message === '401'
    ? 'Session abgelaufen. Bitte neu einloggen.'
    : 'Fehler beim Laden der leeren Boxen.'
);

    } finally {
      setLaden(false);
    }
  }

  useEffect(() => {
    ladeDaten();
  }, []);

  function startEdit(box: LeereBox) {
    setEditId(box.id);
    setEditForm({
      regalnummer: box.regalnummer || '',
      fachnummer: box.fachnummer || '',
    });
  }

  function cancelEdit() {
    setEditId(null);
    setEditForm({ regalnummer: '', fachnummer: '' });
  }

  function handleChange(
    field: keyof EditForm,
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) {
    const value = e.target.value;
    setEditForm((prev) => ({ ...prev, [field]: value }));
  }

  async function saveEdit(id: number) {
    setSpeichernLaeuft(true);
    setFehler(null);

    try {
      const res = await fetchApi(`/api/lagerplaetze/${id}/standort`, {
  method: 'PUT',
  body: JSON.stringify({
    regalnummer: editForm.regalnummer.trim() || null,
    fachnummer: editForm.fachnummer.trim() || null,
  }),
});


      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.error || 'Fehler beim Speichern des Lagerplatzes.');
      }

      setBoxen((prev) =>
        prev.map((b) =>
          b.id === id
            ? {
                ...b,
                regalnummer: editForm.regalnummer.trim() || null,
                fachnummer: editForm.fachnummer.trim() || null,
              }
            : b
        )
      );

      setEditId(null);
      setEditForm({ regalnummer: '', fachnummer: '' });
    } catch (err: any) {
      console.error('❌ Fehler beim Speichern des Lagerplatzes:', err);
      setFehler(
  err?.message === '401'
    ? 'Session abgelaufen. Bitte neu einloggen.'
    : (err.message || 'Fehler beim Speichern des Lagerplatzes.')
);

    } finally {
      setSpeichernLaeuft(false);
    }
  }

  function openDelete(box: LeereBox) {
    setFehler(null);
    setDeleteId(box.id);
    setDeleteBox(box);
  }

  function closeDelete() {
    setDeleteId(null);
    setDeleteBox(null);
  }

  async function confirmDelete() {
    if (!deleteId) return;

    setLoeschenLaeuft(true);
    setFehler(null);

    try {
      const res = await fetchApi(`/api/lagerplaetze/${deleteId}`, {
  method: 'DELETE',
});


      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.error || 'Fehler beim Löschen des Lagerplatzes.');
      }

      setBoxen((prev) => prev.filter((b) => b.id !== deleteId));
      closeDelete();
    } catch (err: any) {
      console.error('❌ Fehler beim Löschen:', err);
      setFehler(
  err?.message === '401'
    ? 'Session abgelaufen. Bitte neu einloggen.'
    : (err.message || 'Fehler beim Löschen des Lagerplatzes.')
);

    } finally {
      setLoeschenLaeuft(false);
    }
  }

  if (laden) {
    return (
      <Container>
        <Box mt={4} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">Leere Boxen</Typography>
        <Button variant="outlined" onClick={ladeDaten}>
          Neu laden
        </Button>
      </Box>

      <Typography variant="body2" sx={{ mb: 2 }}>
        Hier siehst du alle Boxen, deren aktueller Gesamtbestand = 0 ist. Du kannst Regal und Fach
        anpassen, wenn du die Box an einen neuen Lagerplatz stellst. Die Boxnummer bleibt
        erhalten.
      </Typography>

      {fehler && (
        <Box mb={2}>
          <Alert severity="error">{fehler}</Alert>
        </Box>
      )}

      {boxen.length === 0 ? (
        <Alert severity="info">Aktuell gibt es keine leeren Boxen.</Alert>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Lagerort</TableCell>
              <TableCell>Regal</TableCell>
              <TableCell>Fach</TableCell>
              <TableCell>Boxnummer</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {boxen.map((b) => {
              const isEditing = editId === b.id;

              return (
                <TableRow key={b.id}>
                  <TableCell>{b.lagerort_name}</TableCell>
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        variant="standard"
                        value={editForm.regalnummer}
                        onChange={(e) => handleChange('regalnummer', e)}
                        fullWidth
                      />
                    ) : (
                      b.regalnummer || '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        variant="standard"
                        value={editForm.fachnummer}
                        onChange={(e) => handleChange('fachnummer', e)}
                        fullWidth
                      />
                    ) : (
                      b.fachnummer || '-'
                    )}
                  </TableCell>
                  <TableCell>{b.boxnummer}</TableCell>
                  <TableCell align="right">
                    {isEditing ? (
                      <>
                        <Button
                          variant="contained"
                          size="small"
                          sx={{ mr: 1 }}
                          disabled={speichernLaeuft}
                          onClick={() => saveEdit(b.id)}
                        >
                          Speichern
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={speichernLaeuft}
                          onClick={cancelEdit}
                        >
                          Abbrechen
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ mr: 1 }}
                          onClick={() => startEdit(b)}
                        >
                          Bearbeiten
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => openDelete(b)}
                        >
                          Löschen
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Dialog open={deleteId !== null} onClose={closeDelete}>
        <DialogTitle>Box löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Diese Box wird dauerhaft gelöscht. Das ist nur möglich, wenn der Gesamtbestand dieser Box = 0 ist.
          </DialogContentText>
          {deleteBox && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                <b>Lagerort:</b> {deleteBox.lagerort_name}
              </Typography>
              <Typography variant="body2">
                <b>Regal/Fach:</b> {deleteBox.regalnummer || '-'} / {deleteBox.fachnummer || '-'}
              </Typography>
              <Typography variant="body2">
                <b>Boxnummer:</b> {deleteBox.boxnummer}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelete} disabled={loeschenLaeuft}>
            Abbrechen
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={loeschenLaeuft}
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
